import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import clsx from "clsx";
import { FileDown } from "lucide-react";
import { printedAt } from "../lib/plan-export";

/**
 * The head of every printed page: the airport, what the page is, the dates it covers and
 * when and by whom it was printed. A sheet handed to another team is read without the app
 * beside it, so it has to say all of that on its own, in black on white.
 */
export function PrintHeader({
  airport,
  title,
  detail,
  printed,
  by,
  className,
}: {
  /** "YLW · Kelowna International Airport". */
  airport: string;
  title: string;
  /** Lines under the title: the dates, the plan version, anything the reader must not miss. */
  detail: string[];
  /** When it was printed, already in the airport's words and time. */
  printed: string;
  by?: string | null;
  className?: string;
}) {
  return (
    <header className={clsx("mb-3 flex items-start gap-4 border-b-2 border-black pb-2 text-black", className)}>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold tracking-wide uppercase">{airport}</div>
        <h1 className="text-[18px] leading-tight font-bold">{title}</h1>
        {detail.map((d) => (
          <div key={d} className="text-[11.5px]">
            {d}
          </div>
        ))}
      </div>
      <div className="shrink-0 text-right text-[10.5px] leading-snug">
        <div>Printed {printed}</div>
        {by && <div>by {by}</div>}
        <div>Vectro Allocate</div>
      </div>
    </header>
  );
}

/**
 * The time a page is printed, kept current: set when Export PDF is pressed, and again if
 * the browser's own Print is used, so the stamp on the paper is when it was printed rather
 * than when the page was opened.
 */
export function usePrintStamp(tz: string): { stamp: string; print: (title?: string) => void } {
  const [at, setAt] = useState(() => Date.now());
  useEffect(() => {
    const before = () => flushSync(() => setAt(Date.now()));
    window.addEventListener("beforeprint", before);
    return () => window.removeEventListener("beforeprint", before);
  }, []);
  const print = (title?: string) => {
    flushSync(() => setAt(Date.now()));
    // The browser names the saved PDF after the page title, so the file says what it is.
    const was = document.title;
    if (title) document.title = title;
    window.print();
    document.title = was;
  };
  return { stamp: printedAt(at, tz), print };
}

/** The button that prints the page on screen. The browser's print window saves it as a PDF. */
export function ExportPdfButton({ onClick, className, title }: { onClick: () => void; className?: string; title?: string }) {
  return (
    <button
      className={clsx("flex items-center gap-1.5 rounded-md border border-line px-2 py-1 text-[12px] font-semibold text-ink-2 hover:text-ink print:hidden", className)}
      onClick={onClick}
      title={title ?? "Print this page or save it as a PDF: choose Save as PDF in the print window"}
    >
      <FileDown className="size-3.5" /> Export PDF
    </button>
  );
}
